import {Notification, Workspace} from '../../../API';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../conflictResolution/types';
import {applyMerge} from "../applyMerge";

/**
 * Fields that should never be modified by merge (immutable or system-managed)
 */
const IMMUTABLE_FIELDS = ['id', 'ownerId', 'createdAt', '__typename'];

/**
 * Fields that should always come from current state (system-managed)
 */
const SYSTEM_FIELDS = ['version', 'updatedAt', 'deletedAt', '_lastChangedAt'];

/**
 * Conflict handler for Workspace entities.
 * Handles version conflicts during workspace updates with intelligent merging.
 * 
 * Merge Strategy:
 * 1. Start with complete currentState (all server fields)
 * 2. Apply user's intendedChanges on top, except for:
 *    - Immutable fields: id, ownerId, createdAt (never change)
 *    - System fields: version, updatedAt, deletedAt, _lastChangedAt (server-managed)
 * 3. Explicitly set version from currentState (critical for retry)
 * 
 */
export const workspaceConflictHandler: ConflictHandler<Workspace> = {
    entityType: 'Workspace',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<Workspace> {
        // Fallback refetch - server should provide currentState
        throw new Error(
            'Workspace refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<Workspace>): Partial<Workspace> {
        const merged: Partial<Workspace> = {};

        // Start with current state
        Object.assign(merged, currentState);

        applyMerge<Workspace>(merged, intendedChanges, IMMUTABLE_FIELDS, SYSTEM_FIELDS);

        // Ensure version is from current state (critical for retry)
        merged.version = currentState.version;

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};
